/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { type Insight as BaseInsight, registerStandardChartInsight } from '../insightManager';
import { toArray } from '../../util/data';
import {
  findSeriesDataItemInfo,
  mergeSeriesDataItem,
  getDataItemValue
} from '../../helper/standardChart';
import { handleLegendBlur, handleMultipleSeries } from '../helper';

type InsightDataWithSeriesName = {
  /** 高亮的系列名称 */
  seriesName: string | string[];
  /** 弱化系列 */
  blurSeries?: unknown;
};

type InsightDataWithoutSeriesName = {
  /** x 轴指标对应数据集字段 */
  x: string;
  /** y 轴指标对应数据集字段 */
  y: string;
  /** 要标记的 x 轴指标数据值 */
  xValue: string;
  /** 要标记的 y 轴指标数据值 */
  yValue: string;
  /** 弱化系列 */
  blurSeries?: unknown;
};

type InsightData = InsightDataWithSeriesName | InsightDataWithoutSeriesName;

const type = 'Highlight';
const INSIGHT_FLAG = `insight_${type}`;

interface Insight extends BaseInsight<InsightData[]> {
  readonly type: typeof type;
}

registerStandardChartInsight<Insight>({
  type,
  filter: option => {
    // 笛卡尔坐标系布局下，insight的生效范围条件：x、y轴不为空，series类型只包含line、bar、scatter
    const CartesianCoordinatesCondition =
      toArray(option.xAxis).length > 0 &&
      toArray(option.yAxis).length > 0 &&
      !toArray(option.series).some(item => !['line', 'bar', 'scatter'].includes(item.type));

    // 非笛卡尔坐标系布局下，insight的生效范围条件：series类型只包含treemap或pie
    const otherCoorinatesCondition = !toArray(option.series).some(
      item => !['treemap', 'pie'].includes(item.type)
    );
    return CartesianCoordinatesCondition || otherCoorinatesCondition;
  },
  clear: {
    onOptionUpdate(option) {
      option.series = toArray(option.series).map(series => {
        series.markPoint = series.markPoint || {};
        series.markPoint.data = (series.markPoint.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );

        return series;
      });
    }
  },
  render: {
    onOptionUpdate(context, option, legendIns) {
      const insight = context.insightOption;
      const { linkLegend = false } = insight;

      function renderElement(data: InsightDataWithoutSeriesName) {
        const { x, y, xValue, yValue, blurSeries: blurSeriesOpt = {} } = data;

        const dataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          xValue,
          yValue
        });

        if (!dataItemMetaInfo) {
          return;
        }
        const { seriesIndex, dataItemIndex } = dataItemMetaInfo;
        const series = context.util.data.toArray(option.series)[seriesIndex];

        series.markPoint = series.markPoint || {};
        series.markPoint.data = series.markPoint.data || [];

        // * 弱化主图
        if (['bar', 'pie', 'treemap'].includes(series.type)) {
          series.data = series.data.map((dataItem, index) => {
            if (
              [dataItemIndex].includes(index) ||
              (dataItem as unknown as { __noBlur?: boolean })?.__noBlur
            ) {
              return mergeSeriesDataItem(dataItem, { __noBlur: true });
            }

            handleLegendBlur(option, dataItem as unknown as { name: string });
            return mergeSeriesDataItem(dataItem, blurSeriesOpt);
          });
          if (legendIns && option.legend?.data) {
            legendIns.update(option.legend.data);
          }
        } else if (series.type === 'line') {
          option.series = context.util.data.toArray(option.series);
          option.series[seriesIndex] = context.util.data.deepMerge(series, blurSeriesOpt);
        }
      }
      function renderSeries(data: InsightDataWithSeriesName) {
        const { blurSeries = {} } = data;
        const blurSeriesOpt = JSON.parse(JSON.stringify(blurSeries));
        const series = context.util.data.toArray(option.series);
        const seriesIndex = series.findIndex(seriesItem => seriesItem.name === data.seriesName);
        if (!option.series[seriesIndex]?.areaStyle) {
          delete blurSeriesOpt.areaStyle;
        }

        handleMultipleSeries(option, seriesIndex, legendIns!, blurSeriesOpt, linkLegend);
      }
      function render(data: InsightData) {
        // 如果没有seriesName，则渲染element
        if ('seriesName' in data) {
          renderSeries(data);
        } else {
          renderElement(data);
        }
      }

      insight.data.forEach(item => render(item));
    }
  }
});
